import React from 'react';
import {
  Menu,
  Box,
  Typography,
  MenuItem,
  ListItemIcon,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle,
  Settings,
  Logout,
  Notifications,
  Palette,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ThemeToggle } from '@/shared/theme/ThemeToggle';
import { getInitials } from '@/entities/user/model';
import { User } from '@/entities/user';

interface ProfileDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  user: User | null;
  onProfileClick: () => void;
  onSettingsClick: () => void;
  onLogout: () => void;
  onNotificationClick?: () => void;
  unreadNotificationCount?: number;
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  anchorEl,
  open,
  onClose,
  user,
  onProfileClick,
  onSettingsClick,
  onLogout,
  onNotificationClick,
  unreadNotificationCount = 0,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleProfileClick = () => {
    onProfileClick();
    onClose();
  };

  const handleSettingsClick = () => {
    onSettingsClick();
    onClose();
  };

  const handleLogout = () => {
    onLogout();
    onClose();
  };

  const handleNotificationClick = () => {
    if (onNotificationClick) {
      onNotificationClick();
    }
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: { xs: 200, sm: 240 },
          maxWidth: 320,
          mt: 0.5,
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* User Info Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={user?.photoURL || undefined}
            sx={{
              width: 40,
              height: 40,
              fontSize: '0.875rem',
            }}
          >
            {user?.displayName ? (
              getInitials(user.displayName)
            ) : (
              <AccountCircle />
            )}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '0.9375rem',
                fontWeight: 600,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.displayName || 'User'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8125rem',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email || 'No email'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Menu Items */}
      <Box sx={{ py: 0.5 }}>
        {/* Show theme toggle in menu for small mobile */}
        {isSmallMobile && (
          <>
            <MenuItem onClick={onClose} sx={{ py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Palette fontSize="small" />
              </ListItemIcon>
              <Box
                sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  Theme
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <ThemeToggle />
                </Box>
              </Box>
            </MenuItem>

            {onNotificationClick && (
              <MenuItem onClick={handleNotificationClick} sx={{ py: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Notifications fontSize="small" />
                </ListItemIcon>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Notifications
                  </Typography>
                  {unreadNotificationCount > 0 && (
                    <Box
                      sx={{
                        backgroundColor: 'error.main',
                        color: 'error.contrastText',
                        borderRadius: '50%',
                        minWidth: 18,
                        height: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        px: 0.5,
                      }}
                    >
                      {unreadNotificationCount > 99
                        ? '99+'
                        : unreadNotificationCount}
                    </Box>
                  )}
                </Box>
              </MenuItem>
            )}

            <Divider sx={{ my: 0.5 }} />
          </>
        )}

        <MenuItem onClick={handleProfileClick} sx={{ py: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {t('navigation.profile')}
          </Typography>
        </MenuItem>

        <MenuItem onClick={handleSettingsClick} sx={{ py: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Settings fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {t('navigation.settings')}
          </Typography>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {t('auth.logout')}
          </Typography>
        </MenuItem>
      </Box>
    </Menu>
  );
};
